// frontend/src/pages/PublicProfilePage.tsx
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ThemeToggle } from '../components/ThemeToggle';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { User, Calendar, Boxes, ExternalLink, LogOut, LogIn, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { dbSelect } from '../lib/api';
import { CreditBalanceBadge } from '../components/CreditBalanceBadge';

interface PublicProfile {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
}

interface PublicProject {
  id: string;
  name: string;
  description: string | null;
  slug: string | null;
  public_id: string | null;
  is_promoted: boolean;
  monetization_mode: string;
  created_at: string;
  updated_at: string;
}

export function PublicProfilePage() {
  const { username } = useParams<{ username: string }>();
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [projects, setProjects] = useState<PublicProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    if (profile) {
      document.title = `${profile.display_name || profile.username} - Llambada`;
    } else if (username) {
      document.title = `@${username} - Llambada`;
    }
    return () => { document.title = 'Llambada'; };
  }, [profile, username]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!username) {
        setError('Username not provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch profile by username from the secure public_profiles view
        // IMPORTANT: Never query the profiles table directly for public access —
        // it would require an RLS policy that leaks API keys and other sensitive columns.
        const { data: profileData, error: profileError } = await dbSelect(
          'public_profiles',
          {
            select: 'id, username, display_name, avatar_url, created_at',
            filters: {
              username: `ilike.${username}`
            }
          }
        );

        if (profileError) {
          setError('Failed to load profile');
          setLoading(false);
          return;
        }

        if (!profileData || (Array.isArray(profileData) && profileData.length === 0)) {
          setError('Profile not found');
          setLoading(false);
          return;
        }

        const userProfile = (Array.isArray(profileData) ? profileData[0] : profileData) as PublicProfile;
        setProfile(userProfile);

        // Fetch public projects for this user
        const { data: projectsData, error: projectsError } = await dbSelect(
          'projects',
          {
            select: 'id, name, description, slug, public_id, is_promoted, monetization_mode, created_at, updated_at',
            filters: {
              user_id: `eq.${userProfile.id}`,
              is_published: 'eq.true'
            },
            order: 'is_promoted.desc,updated_at.desc'
          }
        );

        if (!projectsError && projectsData) {
          setProjects(Array.isArray(projectsData) ? projectsData as PublicProject[] : []);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('An error occurred');
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-theme-bg">
        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-theme-border-muted bg-theme-bg/80 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-14 sm:h-16 items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo.png" alt="Llambada" className="h-5 w-5 rounded-md shadow-md shadow-accent-500/20" />
                <span className="font-display text-lg sm:text-xl font-semibold text-theme-text">Llambada</span>
              </Link>
              <div className="flex items-center gap-2">
                {user && <CreditBalanceBadge />}
                <ThemeToggle />
                {user ? (
                  <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="btn btn-ghost p-2 md:hidden" aria-label="Toggle menu">
                    {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </button>
                ) : (
                  <Link to="/login" className="btn btn-primary text-sm px-3 py-2">
                    <LogIn className="h-4 w-4" />
                    <span className="hidden sm:inline">Sign in</span>
                  </Link>
                )}
                {user && (
                  <div className="hidden md:flex items-center gap-2">
                    <Link to="/profile" className="btn btn-ghost text-sm" title="Profile & Settings">
                      <User className="h-4 w-4" />
                    </Link>
                    <button onClick={() => signOut()} className="btn btn-ghost text-sm" title="Sign out">
                      <LogOut className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            {showMobileMenu && user && (
              <div className="md:hidden border-t border-theme-border-muted py-3 space-y-1 animate-fade-in">
                <Link to="/profile" className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors" onClick={() => setShowMobileMenu(false)}>
                  <User className="h-4 w-4" />
                  Profile & Settings
                </Link>
                <button onClick={() => { signOut(); setShowMobileMenu(false); }} className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left">
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Error State */}
        <div className="flex flex-col items-center justify-center py-24">
          <User className="h-16 w-16 text-theme-text-muted mb-4" />
          <h1 className="text-2xl font-semibold text-theme-text mb-2">Profile not found</h1>
          <p className="text-theme-text-secondary mb-6">
            This user doesn't exist or their profile is not public.
          </p>
          <Link to="/" className="btn btn-primary">
            Go to homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-theme-border-muted bg-theme-bg/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Llambada" className="h-5 w-5 rounded-md shadow-md shadow-accent-500/20" />
              <span className="font-display text-lg sm:text-xl font-semibold text-theme-text">Llambada</span>
            </Link>
            <div className="flex items-center gap-2">
              {user && <CreditBalanceBadge />}
              <ThemeToggle />
              {user ? (
                <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="btn btn-ghost p-2 md:hidden" aria-label="Toggle menu">
                  {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              ) : (
                <Link to="/login" className="btn btn-primary text-sm px-3 py-2">
                  <LogIn className="h-4 w-4" />
                  <span className="hidden sm:inline">Sign in</span>
                </Link>
              )}
              {user && (
                <div className="hidden md:flex items-center gap-2">
                  <Link to="/profile" className="btn btn-ghost text-sm" title="Profile & Settings">
                    <User className="h-4 w-4" />
                  </Link>
                  <button onClick={() => signOut()} className="btn btn-ghost text-sm" title="Sign out">
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {showMobileMenu && user && (
            <div className="md:hidden border-t border-theme-border-muted py-3 space-y-1 animate-fade-in">
              <Link to="/profile" className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors" onClick={() => setShowMobileMenu(false)}>
                <User className="h-4 w-4" />
                Profile & Settings
              </Link>
              <button onClick={() => { signOut(); setShowMobileMenu(false); }} className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left">
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Profile Content */}
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Profile Header */}
        <div className="flex items-center gap-4 sm:gap-6 mb-8 sm:mb-10">
          <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-theme-surface border border-theme-border-muted flex items-center justify-center shrink-0">
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.display_name || profile.username}
                className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover"
              />
            ) : (
              <User className="h-8 w-8 sm:h-10 sm:w-10 text-theme-text-muted" />
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-theme-text truncate">
              {profile.display_name || profile.username}
            </h1>
            <p className="text-theme-text-secondary">@{profile.username}</p>
            <div className="flex items-center gap-1 mt-1 text-sm text-theme-text-muted">
              <Calendar className="h-4 w-4" />
              <span>Joined {formatDate(profile.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Projects Section */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <Boxes className="h-5 w-5 text-theme-text-muted" />
            <h2 className="text-lg font-semibold text-theme-text">
              Public Mini-Apps
            </h2>
            <span className="text-sm text-theme-text-muted">({projects.length})</span>
          </div>

          {projects.length === 0 ? (
            <div className="text-center py-12 bg-theme-surface rounded-lg border border-theme-border-muted">
              <Boxes className="h-12 w-12 text-theme-text-muted mx-auto mb-4" />
              <p className="text-theme-text-secondary">
                No public mini-apps yet
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {projects.map((project) => (
                <Link
                  key={project.id}
                  to={`/p/${project.public_id}${project.slug ? '/' + project.slug : ''}`}
                  className="group card p-5 hover:border-theme-primary transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-theme-text group-hover:text-theme-primary transition-colors">
                      {project.name}
                    </h3>
                    <ExternalLink className="h-4 w-4 text-theme-text-muted group-hover:text-theme-primary transition-colors" />
                  </div>
                  {project.description && (
                    <p className="text-sm text-theme-text-secondary line-clamp-2 mb-3">
                      {project.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-theme-text-muted">
                    <span>Updated {formatDate(project.updated_at)}</span>
                    {project.is_promoted && (
                      <span className="px-1.5 py-0.5 bg-theme-primary-muted text-theme-primary rounded">
                        Featured
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
